import type { DtoOrderDiscount } from '@repo/api-types/order-retrieval';
import { isObject } from '@repo/utils/type.utils';

export interface SalesPersonDiscount extends DtoOrderDiscount {
    employeeCompanyName?: string;
}

export interface SalesPriceDiscount {
    description: string;
    discountDetails: SalesPriceDiscountDetails;
    type: string;
    value?: number | string;
}

interface SalesPriceDiscountDetails {
    applyOn: string;
    type: string;
    value: number | string;
}

export interface SalesPriceSavings {
    coupons?: number;
    employee?: number;
    manual?: number;
}

export interface SalesPriceCouponInfo {
    discountCode: string;
    discountId: string;
    discountAmount: Array<{
        viewType: string;
        amount: number;
        count: number;
    }>;
    validFromDate: string;
    validToDate: string;
    texts: DiscountText[];
    isServiceDiscount: boolean;
}

interface DiscountText {
    langCode: string;
    name: string;
}

export interface SalesPriceDiscountSummary {
    amount: number;
    couponCode?: string;
    id: string;
    isCoupon?: boolean;
    texts: DiscountText[];
}

export type SalesPriceCouponSummary = SalesPriceDiscountSummary & {
    isCoupon: boolean;
    couponCode: string;
};

export const isSalesPriceDiscount = (value: unknown): value is SalesPriceDiscount => {
    return (
        isObject(value) &&
        'description' in value &&
        isSalesPriceDiscountDetails(value.discountDetails) &&
        'type' in value &&
        typeof value.type === 'string'
    );
};

const isSalesPriceDiscountDetails = (value: unknown): value is SalesPriceDiscountDetails => {
    return isObject(value) && 'applyOn' in value && 'type' in value && 'value' in value;
};

export const isSalesPriceSavings = (value: unknown): value is SalesPriceSavings => {
    if (!value) {
        return false;
    }

    return typeof value === 'object' && ['coupons', 'employee', 'manual'].some((key) => key in value);
};
